import { clearRuntimeTenantId } from '../lib/runtimeTenant'
import {
  computeNextReshowUntil,
  DEFAULT_POPUP_RESHOW_DAILY_AT,
  isDismissUntilActive,
} from '@huay/shared/popupDismissUntil'

const getBaseUrl = () =>
  (import.meta.env.VITE_API_BASE_URL as string) || 'http://localhost:8081'

const POPUP_FETCH_TIMEOUT_MS = 3500

const fetchWithTimeout = async (
  input: RequestInfo | URL,
  init: RequestInit,
  timeoutMs: number = POPUP_FETCH_TIMEOUT_MS
): Promise<Response> => {
  const controller = new AbortController()
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs)

  try {
    return await fetch(input, { ...init, signal: controller.signal })
  } finally {
    window.clearTimeout(timeoutId)
  }
}

export interface PopupData {
  _id: string
  tenantId: string
  title: string
  content: string
  isActive: boolean
  priority: number
  forceShow: boolean
  forceShowUntil?: Date | null
  showOnRoutes: string[]
  popupType: 'info' | 'warning' | 'success' | 'announcement'
  buttonText: string
  allowDismiss: boolean
  autoCloseAfter?: number | null
  startDate?: Date
  endDate?: Date | null
  imageUrl?: string | null
  imagePosition?: 'top' | 'left' | 'right' | 'bottom' | 'background' | 'center'
  imageAlt?: string
  imageStyle?: 'cover' | 'contain' | 'auto' | 'fill'
  displayMode?: 'auto' | 'image_only' | 'text_only' | 'image_text'
  reshowDailyAt?: string
  isForceShow?: boolean
  createdAt: Date
  updatedAt: Date
}

interface ActivePopupsResponse {
  success: boolean
  data: {
    popups: PopupData[]
    count: number
    route: string
    tenantId: string
  }
}

interface DismissResponse {
  success: boolean
  data: {
    dismissal: unknown
    message: string
  }
}

class PopupService {
  private readonly baseUrl: string
  private readonly optOutKeyPrefix = 'popup-optout-'

  constructor() {
    this.baseUrl = getBaseUrl()
  }

  getBaseUrl(): string {
    return this.baseUrl
  }

  private generateClientFingerprint(): string {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (ctx) {
      ctx.textBaseline = 'top'
      ctx.font = '14px Arial'
      ctx.fillText('Client fingerprint', 2, 2)
    }

    const fingerprint = [
      navigator.userAgent,
      navigator.language,
      screen.width + 'x' + screen.height,
      new Date().getTimezoneOffset(),
      canvas.toDataURL(),
    ].join('|')

    let hash = 0
    for (let i = 0; i < fingerprint.length; i += 1) {
      const char = fingerprint.charCodeAt(i)
      hash = (hash << 5) - hash + char
      hash &= hash
    }

    return Math.abs(hash).toString(36)
  }

  private getSessionId(): string {
    let sessionId = localStorage.getItem('popup-session-id')
    if (!sessionId) {
      sessionId = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`
      localStorage.setItem('popup-session-id', sessionId)
    }
    return sessionId
  }

  async getActivePopups(tenantId: string, route: string = '/'): Promise<PopupData[]> {
    try {
      const params = new URLSearchParams({
        tenantId,
        route,
      })

      const response = await fetchWithTimeout(
        `${this.baseUrl}/api/v1/configuration-service/popups/${tenantId}/active?${params}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-Session-ID': this.getSessionId(),
            'X-Client-Fingerprint': this.generateClientFingerprint(),
          },
        }
      )

      if (!response.ok) {
        if (response.status === 429) {
          throw new Error('429')
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const data = (await response.json()) as ActivePopupsResponse
      return data.success ? data.data.popups : []
    } catch (error) {
      console.error('Failed to fetch active popups:', error)
      return []
    }
  }

  async dismissPopup(
    popupId: string,
    tenantId: string,
    dismissType: 'manual' | 'auto' | 'timeout' = 'manual'
  ): Promise<boolean> {
    try {
      const response = await fetchWithTimeout(
        `${this.baseUrl}/api/v1/configuration-service/popups/${tenantId}/${popupId}/dismiss`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Session-ID': this.getSessionId(),
            'X-Client-Fingerprint': this.generateClientFingerprint(),
          },
          body: JSON.stringify({
            tenantId,
            dismissType,
          }),
        },
        5000
      )

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const data = (await response.json()) as DismissResponse
      if (data.success) {
        return true
      }
      return false
    } catch (error) {
      console.error('Failed to dismiss popup:', error)
      return false
    }
  }

  private getOptOutKey(popupId: string, tenantId: string): string {
    return `${this.optOutKeyPrefix}${tenantId}-${popupId}`
  }

  isPopupDismissed(popupId: string, tenantId: string): boolean {
    try {
      const raw = localStorage.getItem(this.getOptOutKey(popupId, tenantId))
      if (!raw) return false
      // Legacy permanent flag → convert to next default reshow window
      if (raw === 'true') {
        const until = computeNextReshowUntil(DEFAULT_POPUP_RESHOW_DAILY_AT)
        localStorage.setItem(this.getOptOutKey(popupId, tenantId), until.toISOString())
        return isDismissUntilActive(until.toISOString())
      }
      if (!isDismissUntilActive(raw)) {
        localStorage.removeItem(this.getOptOutKey(popupId, tenantId))
        return false
      }
      return true
    } catch (error) {
      console.warn('Failed to check popup dismissal state:', error)
      return false
    }
  }

  storeDismissedPopup(popupId: string, tenantId: string, reshowDailyAt?: string | null): void {
    try {
      const until = computeNextReshowUntil(reshowDailyAt)
      localStorage.setItem(this.getOptOutKey(popupId, tenantId), until.toISOString())
    } catch (error) {
      console.warn('Failed to persist dismissed popup:', error)
    }
  }


  clearPopupDismissals(tenantId: string): void {
    try {
      const optOutPrefix = `${this.optOutKeyPrefix}${tenantId}-`
      const prefix = `popup-dismissed-${tenantId}-`
      const legacyKey = `dismissed-popups-${tenantId}`
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith(prefix) || key.startsWith(optOutPrefix)) {
          localStorage.removeItem(key)
        }
      })
      localStorage.removeItem(legacyKey)
    } catch (error) {
      console.warn('Failed to clear popup dismissals:', error)
    }
  }

  clearAllPopupState(): void {
    try {
      Object.keys(localStorage).forEach((key) => {
        if (
          key.startsWith('popup-dismissed-') ||
          key.startsWith(this.optOutKeyPrefix) ||
          key.startsWith('dismissed-popups-') ||
          key === 'popup-system-toggle' ||
          key.startsWith('popup-system-toggle-') ||
          key === 'popup-session-id'
        ) {
          localStorage.removeItem(key)
        }
      })
      clearRuntimeTenantId()
    } catch (error) {
      console.warn('Failed to clear popup state:', error)
    }
  }
}

export const popupService = new PopupService()

